import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';

import DashboardStats from '../components/DashboardStats';
import FirmsTable from '../components/FirmsTable';
import UsersTable from '../components/UsersTable';
import firmService from '../services/firmService';
import userService from '../services/userService';
import '../styles/adminDashboard.css';

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.message ||
  'Something went wrong.';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [firms, setFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [firmData, userData] = await Promise.all([
        firmService.getFirms(),
        userService.getUsers({ role: 'distributor' }),
      ]);

      // Services already extract data from the Axios response.
      // Support either a direct array or an object containing the array.
      const firmsList = Array.isArray(firmData)
        ? firmData
        : Array.isArray(firmData?.firms)
          ? firmData.firms
          : [];

      const usersList = Array.isArray(userData)
        ? userData
        : Array.isArray(userData?.users)
          ? userData.users
          : [];

      setFirms(firmsList);
      setUsers(usersList);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const recentFirms = [...firms]
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, 5);

  const recentUsers = [...users]
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, 5);

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">OVERVIEW</p>
          <h1>Admin Dashboard</h1>
          <p>Manage firms and their distributor accounts.</p>
        </div>

        <div className="admin-header-actions">
          <button
            type="button"
            className="admin-button admin-button-secondary"
            onClick={loadDashboard}
            disabled={loading}
          >
            Refresh
          </button>

          <Link
            to="/admin/firms/create"
            className="admin-button admin-button-primary"
          >
            + Create Firm
          </Link>

          <button
            type="button"
            className="admin-button admin-button-secondary"
            onClick={logout}
          >
            Logout
          </button>
        </div>
      </header>

      {error && (
        <div className="admin-alert admin-alert-error" role="alert">
          {error}
          <button
            type="button"
            onClick={loadDashboard}
            disabled={loading}
          >
            Retry
          </button>
        </div>
      )}

      <DashboardStats
        firms={firms}
        users={users}
        loading={loading}
      />

      <section className="admin-section">
        <div className="admin-section-header">
          <div>
            <h2>Recent Firms</h2>
            <p>Recently created business accounts.</p>
          </div>

          <Link to="/admin/firms" className="admin-text-link">
            View all firms →
          </Link>
        </div>

        <FirmsTable
          firms={recentFirms}
          loading={loading}
          onView={(firm) => {
            navigate(`/admin/firms/${firm.id}`);
          }}
        />
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <div>
            <h2>Recent Distributor Users</h2>
            <p>Recently created distributor login accounts.</p>
          </div>

          <Link to="/admin/users" className="admin-text-link">
            View all users →
          </Link>
        </div>

        <UsersTable
          users={recentUsers}
          loading={loading}
        />
      </section>
    </main>
  );
};

export default AdminDashboard;